import { MoreHorizontal, Pencil, Trash } from 'lucide-react'

import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@ui/web/components'
import { getStaticAssetUrl } from '@helper/utils/file'

import type { Student, StudentAction } from './types'

interface RowActionsProps {
  student: Student
  /** Drives the copy: a school "keluarkan dari sekolah", a family "hapus akun anak". */
  type?: 'web' | 'school'
  /** False for a `member`-role viewer; disables (not hides) the items. */
  canEdit: boolean
  /** Unassigned seats left on the school's active membership — 0 means nothing left to hand out. */
  availableSeats: number
  onAction: (action: StudentAction) => void
}

/**
 * Default row kebab: account settings, hand out a premium seat, remove from the school.
 *
 * A host page with a narrower job — the class roster, whose only row verb is "Keluarkan dari
 * kelas" — passes `renderRowActions` to `StudentTable` instead of using this.
 */
export function RowActions({ student, type = 'school', canEdit, availableSeats, onAction }: RowActionsProps) {
  // A student who already holds a live seat has nothing to gain here; the item is dropped rather
  // than disabled so the menu does not carry a permanently dead row for the premium half of the
  // roster.
  //
  // `heldSeatId` — any live seat, any organization, no lower bound on the window — because one child
  // holds at most ONE seat globally. A student seated by their family (or by another school they
  // attend) has `premiumId === null`, and offering the seat anyway spends a click on a mutation that
  // always refuses: `giveChildPremium` guards with `findActiveSeatForChild`, which is neither
  // organization-scoped nor bounded on `startDate`. This is the same predicate `ManageSeatsDialog`
  // filters its picker with, and it is deliberately NOT the field the Akses badge reads.
  const canGivePremium = student.heldSeatId === null
  const isFamily = type === 'web'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button variant='ghost' className='srd:h-8 srd:w-8 srd:p-0'>
            <span className='srd:sr-only'>Buka menu</span>
            <MoreHorizontal className='srd:h-4 srd:w-4' />
          </Button>
        }
      />

      <DropdownMenuContent align='end' className='srd:w-fit srd:max-w-[250px]'>
        <DropdownMenuItem disabled={!canEdit} onClick={() => onAction({ kind: 'settings', student })}>
          <Pencil className='srd:mr-2 srd:h-4 srd:w-4' />
          {isFamily ? 'Pengaturan akun' : 'Pengaturan akun murid'}
        </DropdownMenuItem>

        {canGivePremium && (
          <DropdownMenuItem
            disabled={!canEdit || availableSeats === 0}
            className='srd:flex srd:items-center srd:text-purple-100'
            onClick={() => onAction({ kind: 'give-premium', student })}
          >
            <div className='srd:bg-purple-100 srd:mr-2 srd:flex srd:h-[20px] srd:w-[20px] srd:items-center srd:justify-center srd:rounded-full'>
              <img
                src={getStaticAssetUrl('illustration/bintang-senyum.svg')}
                alt='Star'
                width={16}
                height={16}
                draggable={false}
              />
            </div>
            Berikan kursi premium
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          variant='destructive'
          disabled={!canEdit}
          onClick={() => onAction({ kind: 'remove', student })}
        >
          <Trash className='srd:mr-2 srd:h-4 srd:w-4' />
          {isFamily ? 'Hapus akun anak' : 'Keluarkan dari sekolah'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

interface EditStudentButtonProps {
  student: Student
  /** False when the viewer may not edit this student; the button stays visible but inert. */
  canEdit: boolean
  onAction: (action: StudentAction) => void
}

/**
 * The whole row menu reduced to its one verb: open the student's account settings.
 *
 * Used where the school-level items in `RowActions` — handing out a premium seat, removing the
 * student from the school — are not this page's job. Those live on Kelola murid.
 */
export function EditStudentButton({ student, canEdit, onAction }: EditStudentButtonProps) {
  return (
    <Button
      variant='ghost'
      className='srd:h-8 srd:w-8 srd:p-0 srd:disabled:cursor-not-allowed'
      disabled={!canEdit}
      aria-label={`Pengaturan akun ${student.name}`}
      onClick={() => onAction({ kind: 'settings', student })}
    >
      <Pencil className='srd:h-4 srd:w-4' />
    </Button>
  )
}
